import { assistantRequest } from "./assistant";
import { authCacheScope } from "./cacheScope";
import { apiBaseUrl } from "./pocketbase";

export const INTERNAL_PAGES = ["operations", "metrics-contexts", "ai-chat", "ai-prompts", "workspace-access", "analysis-runs", "report-import"] as const;
export type InternalPage = typeof INTERNAL_PAGES[number];
export interface AccountUsage {
  period: string; starts_at: string; resets_at: string;
  used: number; limit: number | null; remaining: number | null;
  historical_usage_excluded: boolean; generation_configured: boolean;
  token_allowance?: { limit: number | null; charged: number; held: number; remaining: number | null;
    reservation: number; can_admit: boolean; prior_requests_excluded: number };
  tokens: { total?: number | null; known_total_requests?: number; input: number | null; output: number | null; requests: number;
    known_input_requests: number; known_output_requests: number; complete: boolean };
}
export const accountKeys = {
  all: () => ["account", apiBaseUrl, authCacheScope()] as const,
  usage: () => [...accountKeys.all(), "usage"] as const,
  history: (days: number) => [...accountKeys.all(), "history", days] as const,
  navigation: () => [...accountKeys.all(), "navigation"] as const,
};
const integer = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) >= 0;
export function parseAccountUsage(value: unknown): AccountUsage {
  const v = value as AccountUsage;
  const t = v?.tokens;
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v.period) ||
    v.starts_at !== `${v.period}T00:00:00.000Z` || !Number.isFinite(Date.parse(v.starts_at)) ||
    Date.parse(v.resets_at) - Date.parse(v.starts_at) !== 86400000 ||
    !integer(v.used) || !(v.limit === null || integer(v.limit)) ||
    v.remaining !== (v.limit === null ? null : Math.max(0, v.limit - v.used)) ||
    typeof v.historical_usage_excluded !== "boolean" || typeof v.generation_configured !== "boolean" || !t ||
    !integer(t.requests) || t.requests !== v.used || !integer(t.known_input_requests) || !integer(t.known_output_requests) ||
    t.known_input_requests > t.requests || t.known_output_requests > t.requests ||
    !(t.input === null || integer(t.input)) || !(t.output === null || integer(t.output)) ||
    (t.input === null) !== (t.requests > 0 && t.known_input_requests === 0) ||
    (t.output === null) !== (t.requests > 0 && t.known_output_requests === 0) ||
    t.complete !== (t.known_input_requests === t.requests && t.known_output_requests === t.requests)) {
    throw new TypeError("Invalid account usage");
  }
  const a = v.token_allowance;
  if (a && (!(a.limit === null || integer(a.limit)) || !integer(a.charged) || !integer(a.held) ||
      !integer(a.reservation) || a.reservation === 0 || !integer(a.prior_requests_excluded) || a.prior_requests_excluded > v.used ||
      a.remaining !== (a.limit === null ? null : Math.max(0, a.limit - a.charged - a.held)) ||
      a.can_admit !== (a.remaining === null || a.remaining >= a.reservation))) throw new TypeError("Invalid token allowance");
  return v;
}
export async function getAccountUsage(signal?: AbortSignal): Promise<AccountUsage> {
  return parseAccountUsage(await assistantRequest("account/ai-usage", undefined, signal));
}
export async function getAccountNavigation(signal?: AbortSignal): Promise<InternalPage[]> {
  const value = await assistantRequest("account/navigation", undefined, signal) as { pages: unknown };
  if (!value || !Array.isArray(value.pages) || value.pages.length > INTERNAL_PAGES.length ||
    value.pages.some(p => !INTERNAL_PAGES.includes(p)) || new Set(value.pages).size !== value.pages.length) {
    throw new TypeError("Invalid account navigation");
  }
  return value.pages as InternalPage[];
}

export type UsageDays = 7 | 30 | 90;
export interface UsageHistory {
  days: UsageDays; starts_at: string; ends_at: string; coverage_since: string;
  coverage: "retained_records_only"; legacy_included: boolean;
  items: { period: string; requests: number | null; legacy_requests: number;
    coverage: "unavailable" | "retained_records"; tokens: AccountUsage["tokens"] }[];
}
export async function getUsageHistory(days: UsageDays, signal?: AbortSignal): Promise<UsageHistory> {
  if (![7, 30, 90].includes(days)) throw new TypeError("Invalid history range");
  return parseUsageHistory(await assistantRequest(`account/ai-usage/history?days=${days}`, undefined, signal), days);
}
export function parseUsageHistory(value: unknown, days: UsageDays): UsageHistory {
  const v = value as UsageHistory;
  if (!v || v.days !== days || !Array.isArray(v.items) || v.items.length !== days ||
      v.coverage !== "retained_records_only" || typeof v.legacy_included !== "boolean" ||
      !/^\d{4}-\d{2}-\d{2}$/.test(v.coverage_since) || !Number.isFinite(Date.parse(v.starts_at)) ||
      Date.parse(v.ends_at) - Date.parse(v.starts_at) !== days * 86400000) throw new TypeError("Invalid usage history");
  for (const [i, row] of v.items.entries()) {
    const t = row.tokens;
    if (row.period !== new Date(Date.parse(v.starts_at) + i * 86400000).toISOString().slice(0, 10) ||
        !(row.requests === null || integer(row.requests)) || !integer(row.legacy_requests) ||
        !["unavailable", "retained_records"].includes(row.coverage) ||
        (row.requests === null) !== (row.coverage === "unavailable") || !t || !integer(t.requests) ||
        row.requests !== null && t.requests !== row.requests ||
        !integer(t.known_input_requests) || !integer(t.known_output_requests) || !integer(t.known_total_requests) ||
        t.known_input_requests > t.requests || t.known_output_requests > t.requests || t.known_total_requests > Math.min(t.known_input_requests,t.known_output_requests) ||
        ![t.input,t.output,t.total].every(n => n === null || integer(n)) ||
        row.requests !== null && t.complete !== (t.known_input_requests === t.requests && t.known_output_requests === t.requests))
      throw new TypeError("Invalid usage history row");
  }
  return v;
}
